import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { useCreateBrand } from 'src/graphql/entities/brand';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { ClientSegmentDialogShell } from './client-segment-dialog-shell';
import { ClientSegmentFormFields } from './client-segment-form-fields';
import {
  ClientSegmentFormField,
  type ClientSegmentFormValues,
  DEFAULT_CLIENT_SEGMENT_FORM_VALUES,
} from './client-segment-form.types';

export const ClientSegmentAddModal = () => {
  const { isOpened, modalProps } = useModalState(Modals.ClientSegmentAddModal);
  const { closeModal } = useModalMethods(Modals.ClientSegmentAddModal);
  const { showSuccessAlert } = useNotifications();
  const { createBrand } = useCreateBrand();

  const partnerId = modalProps?.partnerId ?? null;

  const formMethods = useForm<ClientSegmentFormValues>({
    defaultValues: DEFAULT_CLIENT_SEGMENT_FORM_VALUES,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = formMethods;

  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const close = useCallback(() => {
    reset(DEFAULT_CLIENT_SEGMENT_FORM_VALUES);
    setSubmissionError(null);
    closeModal();
  }, [closeModal, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (partnerId === null) {
      setSubmissionError('No partner selected.');
      return;
    }
    setSubmissionError(null);

    const created = await createBrand({
      partnerId,
      name: values[ClientSegmentFormField.Name].trim(),
      displayName: values[ClientSegmentFormField.DisplayName].trim(),
    });

    if (!created) {
      setSubmissionError('Could not create client segment.');
      return;
    }

    showSuccessAlert('Client segment created.');
    close();
  });

  if (!isOpened) return null;

  const actions = (
    <Stack direction="row" spacing={1} ml="auto">
      <Button onClick={close} disabled={isSubmitting}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        onClick={onSubmit}
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        Create segment
      </LoadingButton>
    </Stack>
  );

  return (
    <ClientSegmentDialogShell
      open
      title="Add client segment"
      subtitle="Create a brand-facing identity for one of your audiences."
      error={submissionError}
      actions={actions}
      onClose={close}
    >
      <GenericForm<ClientSegmentFormValues> formMethods={formMethods}>
        <ClientSegmentFormFields disabled={isSubmitting} />
      </GenericForm>
    </ClientSegmentDialogShell>
  );
};
